/** Contexte multi-société : liste, courante, create / update. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { facturxFetch } from "../api/client";
import type { CompanyDraft } from "../features/settings/types";
import type { CompanyFormats } from "../formats/types";
import type { CompanyPayments } from "../payments/types";
import type { CompanyModel } from "../model/types";
import type { CompanyMail } from "../email/types";
import type { CompanyPortal } from "../portal/types";
import type { CompanyNumbering } from "../numbering/types";
import type { CompanyProductSettings } from "../products/settings";
import type { CompanyWorkflow } from "../workflow/types";
import type { Company } from "../types";
import { clearCompanyId, persistCompanyId, readCompanyId, announceFacturxCompany, listenFacturxCompanyAsk } from "./companyId";
import type { CompanyCreateInput } from "./createInput";

type CompanyCtx = {
  companies: Company[];
  company: Company | null;
  loading: boolean;
  error: string;
  setCompanyId: (id: number) => void;
  reload: () => Promise<Company[]>;
  createCompany: (input: CompanyCreateInput) => Promise<Company>;
  updateCompany: (id: number, patch: CompanyDraft) => Promise<Company>;
  updateFormats: (id: number, patch: CompanyFormats) => Promise<Company>;
  updatePayments: (id: number, patch: CompanyPayments) => Promise<Company>;
  updateWorkflow: (id: number, patch: CompanyWorkflow) => Promise<Company>;
  updateProductSettings: (id: number, patch: CompanyProductSettings) => Promise<Company>;
  updateModel: (id: number, patch: CompanyModel) => Promise<Company>;
  updateNumbering: (id: number, patch: CompanyNumbering) => Promise<Company>;
  updateEmail: (id: number, patch: CompanyMail) => Promise<Company>;
  updatePortal: (id: number, patch: CompanyPortal) => Promise<Company>;
  deleteCompany: (id: number, confirm_name: string) => Promise<void>;
};

const Ctx = createContext<CompanyCtx | null>(null);

export function CompanyProvider({ userId, children }: { userId: string; children: ReactNode }) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyIdState] = useState<number | null>(readCompanyId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    const data = await facturxFetch<{ companies: Company[] }>("companies.php");
    const list = data.companies.map((c) => ({
      ...c,
      id: Number(c.id),
      owner_user_id: c.owner_user_id != null ? Number(c.owner_user_id) : undefined,
      is_owner: c.is_owner !== false,
    }));
    setCompanies(list);
    return list;
  }, []);

  useEffect(() => {
    let stop = false;
    setLoading(true);
    reload()
      .then((list) => {
        if (stop) return;
        setError("");
        setCompanyIdState((cur) => {
          if (cur && list.some((c) => c.id === cur)) return cur;
          const first = list[0]?.id ?? null;
          if (first) persistCompanyId(first);
          return first;
        });
      })
      .catch((e) => {
        if (!stop) setError(e instanceof Error ? e.message : "Erreur");
      })
      .finally(() => {
        if (!stop) setLoading(false);
      });
    return () => {
      stop = true;
    };
  }, [userId, reload]);

  const setCompanyId = useCallback((id: number) => {
    persistCompanyId(id);
    setCompanyIdState(id);
  }, []);

  useEffect(() => {
    listenFacturxCompanyAsk();
    if (companyId) announceFacturxCompany(companyId);
  }, [companyId]);

  const createCompany = useCallback(
    async (input: CompanyCreateInput) => {
      const data = await facturxFetch<{ company: Company }>("companies.php", {
        method: "POST",
        body: JSON.stringify(input),
      });
      const created = { ...data.company, id: Number(data.company.id) };
      persistCompanyId(created.id);
      setCompanyIdState(created.id);
      await reload();
      return created;
    },
    [reload]
  );

  const updateCompany = useCallback(
    async (id: number, patch: CompanyDraft) => {
      const data = await facturxFetch<{ company: Company }>("companies.php", {
        method: "PATCH",
        body: JSON.stringify({ id, ...patch }),
      });
      await reload();
      return data.company;
    },
    [reload]
  );

  const updateFormats = useCallback(
    async (id: number, patch: CompanyFormats) => {
      const data = await facturxFetch<{ company: Company }>("companies.php", {
        method: "PATCH",
        body: JSON.stringify({ id, scope: "formats", ...patch }),
      });
      await reload();
      return data.company;
    },
    [reload]
  );

  const deleteCompany = useCallback(
    async (id: number, confirm_name: string) => {
      await facturxFetch("companies.php", {
        method: "DELETE",
        body: JSON.stringify({ id, confirm_name }),
      });
      const list = await reload();
      const next = list[0]?.id ?? null;
      if (next) {
        persistCompanyId(next);
        setCompanyIdState(next);
      } else {
        clearCompanyId();
        setCompanyIdState(null);
      }
    },
    [reload]
  );

  const updateModel = useCallback(
    async (id: number, patch: CompanyModel) => {
      const data = await facturxFetch<{ company: Company }>("companies.php", {
        method: "PATCH",
        body: JSON.stringify({ id, scope: "model", ...patch }),
      });
      await reload();
      return data.company;
    },
    [reload]
  );

  const updateNumbering = useCallback(
    async (id: number, patch: CompanyNumbering) => {
      const data = await facturxFetch<{ company: Company }>("companies.php", {
        method: "PATCH",
        body: JSON.stringify({ id, scope: "numbering", numbering: patch }),
      });
      await reload();
      return data.company;
    },
    [reload]
  );

  const updateEmail = useCallback(
    async (id: number, patch: CompanyMail) => {
      const data = await facturxFetch<{ company: Company }>("companies.php", {
        method: "PATCH",
        body: JSON.stringify({ id, scope: "email", mail: patch }),
      });
      await reload();
      return data.company;
    },
    [reload]
  );

  const updatePortal = useCallback(
    async (id: number, patch: CompanyPortal) => {
      const data = await facturxFetch<{ company: Company }>("companies.php", {
        method: "PATCH",
        body: JSON.stringify({ id, scope: "portal", portal: patch }),
      });
      await reload();
      return data.company;
    },
    [reload]
  );

  const updateProductSettings = useCallback(
    async (id: number, patch: CompanyProductSettings) => {
      const data = await facturxFetch<{ company: Company }>("companies.php", {
        method: "PATCH",
        body: JSON.stringify({ id, scope: "products", ...patch }),
      });
      await reload();
      return data.company;
    },
    [reload]
  );

  const updateWorkflow = useCallback(
    async (id: number, patch: CompanyWorkflow) => {
      const data = await facturxFetch<{ company: Company }>("companies.php", {
        method: "PATCH",
        body: JSON.stringify({ id, scope: "workflow", ...patch }),
      });
      await reload();
      return data.company;
    },
    [reload]
  );

  const updatePayments = useCallback(
    async (id: number, patch: CompanyPayments) => {
      const data = await facturxFetch<{ company: Company }>("companies.php", {
        method: "PATCH",
        body: JSON.stringify({ id, scope: "payments", ...patch }),
      });
      await reload();
      return data.company;
    },
    [reload]
  );

  const company = companies.find((c) => c.id === companyId) ?? companies[0] ?? null;

  const value = useMemo(
    () => ({
      companies,
      company,
      loading,
      error,
      setCompanyId,
      reload,
      createCompany,
      updateCompany,
      updateFormats,
      updatePayments,
      updateWorkflow,
      updateProductSettings,
      updateModel,
      updateNumbering,
      updateEmail,
      updatePortal,
      deleteCompany,
    }),
    [companies, company, loading, error, setCompanyId, reload, createCompany, updateCompany, updateFormats, updatePayments, updateWorkflow, updateProductSettings, updateModel, updateNumbering, updateEmail, updatePortal, deleteCompany]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCompany(): CompanyCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useCompany");
  return ctx;
}
